import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PuzzleSlot from "./PuzzleSlot";
import PuzzlePiece from "./PuzzlePiece";
import PuzzleTray from "./PuzzleTray";

const BANK_TILE_BY_GRID = { 3: 62, 4: 52, 5: 44, 6: 38 };

/**
 * The square N×N board plus the piece bank. Owns the forgiving drag mechanic:
 * pick a piece up from any slot or the bank, drop it on any slot (occupied slots
 * swap) or off the board (back to the bank). Auto-completion is judged by the
 * page from the resulting slots.
 */
const PuzzleBoard = ({
  slots,
  bank,
  grid,
  boardImage,
  composited,
  disabled,
  preparing,
  onDrop,
  onPickup,
}) => {
  const wrapRef = useRef(null);
  const slotRefs = useRef({});
  const [boardWidth, setBoardWidth] = useState(0);
  const [drag, setDrag] = useState(null);
  // Drag movement never touches React state: pointermove writes a transform
  // straight to the floating piece's DOM node (rAF-coalesced). With N² clipped
  // + drop-shadowed pieces, per-event re-renders made large grids laggy.
  const floatRef = useRef(null);
  const pointRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      if (el) setBoardWidth(el.clientWidth);
      return undefined;
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setBoardWidth(w);
    });
    ro.observe(el);
    setBoardWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const boardTile = boardWidth > 0 ? boardWidth / grid : 60;
  const bankTile = BANK_TILE_BY_GRID[grid] || 40;

  const registerRef = useCallback((position, el) => {
    slotRefs.current[position] = el;
  }, []);

  const handlePointerDown = useCallback(
    (e, piece, from) => {
      if (disabled) return;
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      onPickup?.();
      setDrag({
        piece,
        from,
        x: e.clientX,
        y: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        tile: from.kind === "slot" ? boardTile : bankTile,
      });
    },
    [disabled, onPickup, boardTile, bankTile]
  );

  useEffect(() => {
    if (!drag) return undefined;

    const applyTransform = () => {
      rafRef.current = 0;
      const el = floatRef.current;
      if (el) {
        el.style.transform = `translate3d(${pointRef.current.x - drag.offsetX}px, ${
          pointRef.current.y - drag.offsetY
        }px, 0)`;
      }
    };
    const move = (e) => {
      pointRef.current = { x: e.clientX, y: e.clientY };
      // Coalesce to one DOM write per frame regardless of pointer event rate.
      if (!rafRef.current) rafRef.current = requestAnimationFrame(applyTransform);
    };
    const up = (e) => {
      window.removeEventListener("pointermove", move);
      const point = { x: e.clientX, y: e.clientY };
      let target = null;
      let best = null;
      let bestDist = Infinity;
      Object.entries(slotRefs.current).forEach(([idxStr, el]) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (point.x >= r.left && point.x <= r.right && point.y >= r.top && point.y <= r.bottom) {
          target = Number(idxStr);
        }
        const d = Math.hypot(point.x - (r.left + r.width / 2), point.y - (r.top + r.height / 2));
        if (d < bestDist) {
          bestDist = d;
          best = { pos: Number(idxStr), width: r.width };
        }
      });
      if (target == null && best && bestDist < best.width) target = best.pos;
      onDrop(drag.piece, drag.from, target);
      setDrag(null);
    };

    pointRef.current = { x: drag.x, y: drag.y };
    applyTransform();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, onDrop]);

  return (
    <section className="relative">
      <div
        ref={wrapRef}
        className="mx-auto"
        style={{ width: "min(100%, 520px)", aspectRatio: "1 / 1" }}
      >
        <div
          className="grid"
          style={{
            width: boardWidth || "100%",
            height: boardWidth || "100%",
            gridTemplateColumns: `repeat(${grid}, 1fr)`,
            gridTemplateRows: `repeat(${grid}, 1fr)`,
            overflow: "visible",
          }}
        >
          {slots.map((piece, position) => (
            <PuzzleSlot
              key={`slot-${position}`}
              position={position}
              piece={piece}
              grid={grid}
              tile={boardTile}
              boardImage={boardImage}
              composited={composited}
              isCorrect={piece === position}
              isDragging={drag?.piece === piece && drag?.from?.kind === "slot"}
              disabled={disabled}
              onPiecePointerDown={handlePointerDown}
              registerRef={registerRef}
            />
          ))}
        </div>
      </div>

      <div className="mx-auto" style={{ maxWidth: 720 }}>
        <PuzzleTray
          bank={bank}
          grid={grid}
          tile={bankTile}
          boardImage={boardImage}
          composited={composited}
          disabled={disabled}
          draggingPiece={drag?.from?.kind === "bank" ? drag?.piece : null}
          preparing={preparing}
          onPiecePointerDown={handlePointerDown}
        />
      </div>

      {drag && (
        <div
          ref={floatRef}
          className="pointer-events-none fixed left-0 top-0 z-[500]"
          style={{
            transform: `translate3d(${drag.x - drag.offsetX}px, ${drag.y - drag.offsetY}px, 0)`,
            willChange: "transform",
          }}
        >
          <PuzzlePiece
            boardImage={boardImage}
            composited={composited}
            index={drag.piece}
            grid={grid}
            tile={drag.tile}
            lifted
          />
        </div>
      )}
    </section>
  );
};

export default PuzzleBoard;
